import React from 'react';

/**
 * Officials Scheduling add-on manual, rendered at /manual/officials.
 * Part 1 = organizers & schedulers, Part 2 = officials.
 * Keep in sync with the Officials section of PLATFORM_KNOWLEDGE in backend/routers/assistant.py.
 */

const P = (props) => <p className="text-slate-700 leading-relaxed" {...props} />;
const UL = (props) => <ul className="list-disc pl-6 space-y-1.5 text-slate-700" {...props} />;
const OL = (props) => <ol className="list-decimal pl-6 space-y-1.5 text-slate-700" {...props} />;
const H3 = (props) => <h3 className="text-lg font-bold text-slate-900 mt-5 mb-2" {...props} />;
const B = ({ children }) => <strong className="text-slate-900 font-semibold">{children}</strong>;
const Code = ({ children }) => <code className="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-sm">{children}</code>;
const Tip = ({ children }) => (
  <div className="rounded-xl border-l-4 border-amber-400 bg-amber-50 px-4 py-2.5 text-sm text-amber-950 break-inside-avoid">{children}</div>
);
const Terms = ({ rows }) => (
  <div className="rounded-lg border divide-y overflow-hidden break-inside-avoid not-prose">
    {rows.map(([k, v]) => (
      <div key={k} className="grid grid-cols-1 sm:grid-cols-[minmax(150px,28%)_1fr] text-sm">
        <div className="px-3 py-2 font-semibold text-slate-900 bg-slate-50">{k}</div>
        <div className="px-3 py-2 text-slate-700">{v}</div>
      </div>
    ))}
  </div>
);

const ORG = 'Part 1 · For organizers & schedulers';
const OFF = 'Part 2 · For officials';

export const OFFICIALS_MANUAL_SECTIONS = [
  {
    id: 'org-overview',
    part: ORG,
    title: '1. Overview & access',
    searchText: 'add-on price 299 paypal subscribe renew auto-renew owner scheduler collaborator roles hub organization switch who can',
    body: (
      <>
        <P>
          The <B>Officials Scheduling</B> add-on lets you schedule and pay referees, umpires, assistant referees,
          linespeople, table officials, scorekeepers and timekeepers on the games already in your Schedule Guru schedules.
          No double entry: every game appears automatically.
        </P>
        <H3>Price & subscription</H3>
        <UL>
          <li><B>$299 USD per year</B>, billed yearly by card (Visa or Mastercard) or PayPal. It works with any plan, including Free.</li>
          <li>Officials <B>never pay</B>. They use a free account.</li>
          <li>Payment tracking (fees, approvals, payouts, reports) is included at no extra cost.</li>
          <li>Subscribe from the <B>Officials</B> page: user menu → <B>Officials Hub</B>, then <B>Subscribe</B> and choose card or PayPal.</li>
          <li>Manage it under <B>Settings → Your add-on</B>. <B>Turn off auto-renew</B> keeps access until the end of your paid year, or choose <B>Renew for another year</B>.</li>
        </UL>
        <H3>Who can do what</H3>
        <Terms rows={[
          ['Owner', 'The account that bought the add-on. Full access, including schedulers, payment permissions and billing.'],
          ['Scheduler', 'Added by email in Settings. Can assign officials and manage the officials list, availability and settings. Sees money only if the owner turns on payment permissions.'],
          ['Schedule collaborator', 'An editor on one of your schedules. Sees the Games and Availability tabs for those schedules only.'],
          ['Official', 'Uses the free Officials Portal to answer assignments, share availability and see earnings.'],
        ]} />
        <H3>The Officials Hub</H3>
        <P>
          Open it at <Code>/officials</Code> (user menu → <B>Officials Hub</B>). Tabs: <B>Games</B>, <B>Officials</B>,
          <B> Availability</B>, <B>Payments</B> (only with payment access) and <B>Settings</B>. If you manage more than
          one organization, switch with the selector at the top. If you also officiate, <B>My officiating</B> opens
          your own portal. The <B>Manual</B> button brings you back here.
        </P>
        <Tip>Recommended order the first time: Settings → Officials → Availability → Games → Payments.</Tip>
      </>
    ),
  },
  {
    id: 'org-setup',
    part: ORG,
    title: '2. Settings & setup',
    searchText: 'roles positions crew officials needed per game sport default custom game length travel buffer reminders 24 hours 3 hours sign-up link regenerate schedulers invite manual link payment permissions',
    body: (
      <>
        <H3>Official roles</H3>
        <P>Rename, add or remove the positions you assign. Renaming a role keeps existing assignments.</P>
        <H3>Officials needed per game</H3>
        <P>
          Each schedule has a crew, and the default comes from the sport. For example, basketball needs 2 referees, 1
          scorekeeper and 1 timekeeper, soccer needs 1 referee and 2 assistant referees, and netball needs 2 umpires, 1
          scorekeeper and 1 timekeeper. Click <B>Edit</B> to set a custom crew, or <B>Use sport default</B> to go back.
        </P>
        <H3>Timing & reminders</H3>
        <UL>
          <li><B>Game length (min)</B>: default 90. Used to detect overlapping games.</li>
          <li><B>Travel buffer (min)</B>: default 30. The minimum gap between games at different venues.</li>
          <li><B>Send reminder emails before each game</B>: choose any of 7 days, 2 days, 24 hours, 6 hours, 3 hours or 1 hour (default 24 hours + 3 hours). Reminders follow reschedules automatically.</li>
        </UL>
        <H3>Officials sign-up link</H3>
        <P>
          Share this link (by WhatsApp, a group chat or your website) so officials can add themselves to your list with a free
          account. <B>Make a new link</B> creates a fresh one, and the old link stops working.
        </P>
        <H3>Officials schedulers (owner only)</H3>
        <OL>
          <li>Type the person's email and click <B>+</B>.</li>
          <li>Leave <B>Email them an invite</B> ticked to send a link to the Officials Hub. Tick <B>Include a link to the Officials manual</B> to add this manual to the email.</li>
          <li>They sign in, or create a free account, with that exact email. Use the envelope icon next to a scheduler to send the invite again.</li>
        </OL>
        <H3>Payment permissions (owner only)</H3>
        <P>
          Schedulers see no money until you allow it. There are six toggles per scheduler, all off by default: <B>View payments</B>,
          <B> Manage rates & settings</B>, <B>Verify games</B>, <B>Approve payments</B>, <B>Record payments</B>, <B>Export reports</B>.
          Turning any one on also turns on View. Turning View off clears the rest. Click <B>Save permissions</B>.
        </P>
      </>
    ),
  },
  {
    id: 'org-officials',
    part: ORG,
    title: '3. Building your officials list',
    searchText: 'add official import csv excel template invite send invite resend joined not invited inactive remove delete photo certifications positions notes details history',
    body: (
      <>
        <H3>Add officials</H3>
        <UL>
          <li><B>Add official</B>: first and last name, email, phone, photo, sports, certifications, preferred positions, private notes, and active or inactive. Contact details are only visible to you and your schedulers.</li>
          <li><B>Import CSV / Excel</B>: download the template, fill it in and upload it. A row with an email already on your list updates that official instead of creating a duplicate.</li>
          <li><B>Copy sign-up link</B>: officials add themselves.</li>
        </UL>
        <H3>Invites</H3>
        <P>
          Each official shows <B>Not invited</B>, <B>Invited</B> or <B>Joined</B>. Use <B>Send invite</B> (or tick several and click
          <B> Invite N</B>) to email each one their personal join link. <B>Availability set</B> appears once they have submitted it.
        </P>
        <H3>Details, edit & remove</H3>
        <UL>
          <li><B>View details</B>: profile, upcoming and past games, availability, and the personal invite link (<B>Copy link</B> / <B>Email invite</B>).</li>
          <li><B>Inactive</B> keeps the official on your list, but you're warned before assigning them.</li>
          <li><B>Remove</B> takes them off your list and cancels their upcoming assignments. They get a cancellation email.</li>
        </UL>
      </>
    ),
  },
  {
    id: 'org-availability',
    part: ORG,
    title: '4. Availability',
    searchText: "who's available grid partly blocked unavailable no info not submitted no account availability request remind follow up date range",
    body: (
      <>
        <H3>Who's available</H3>
        <P>A grid of every official by day. Each official shows as:</P>
        <Terms rows={[
          ['Available', 'Free for that day, based on their weekly hours or date overrides.'],
          ['Partly blocked', 'Free for only part of the day.'],
          ['Unavailable', 'Marked unavailable.'],
          ['No info / Not submitted', 'Has an account but hasn\'t shared availability.'],
          ['No account yet', 'Hasn\'t joined the portal, so they can\'t share availability.'],
        ]} />
        <H3>Availability requests</H3>
        <OL>
          <li>Click <B>New request</B>. Give it a title (for example "Spring league games"), a date range and an optional message.</li>
          <li>Send it to all active officials or choose specific ones. Each gets an email that links to their portal.</li>
          <li>Responses are tracked per request. <B>Remind</B> emails only the people who haven't answered yet.</li>
        </OL>
      </>
    ),
  },
  {
    id: 'org-assigning',
    part: ORG,
    title: '5. Assigning officials (Games tab)',
    searchText: 'games table calendar upcoming past all played correct fix past game record filter staffed partially no officials pending confirmations declined conflicts assign official replace remove bulk copy crew send emails mark accepted phone whatsapp history cancel game restore export indicator available unknown conflict warning override travel instructions fee',
    body: (
      <>
        <P>
          Every game in your schedules appears here automatically. Switch between <B>Table</B> and <B>Calendar</B>, choose
          <B> Upcoming</B> (the default), <B>Past</B> (newest first) or <B>All</B>, and filter by competition, venue, official or status. The status counters (<B>No officials</B>, <B>Partially staffed</B>, <B>Fully staffed</B>,
          <B> Pending confirmations</B>, <B>Declined</B>, <B>Conflicts</B>) show what needs attention. <B>Export</B> downloads CSV or Excel.
        </P>
        <H3>Assign an official</H3>
        <OL>
          <li>Click <B>Assign official</B> on a game and pick the role.</li>
          <li>Search the list. Each official shows a live indicator: <B>Available</B>, <B>Unavailable</B>, <B>Unknown</B> (no availability given) or <B>Scheduling Conflict</B>.</li>
          <li>Optionally add special instructions (for example "arrive 30 min early"), then save. The official gets an email right away.</li>
        </OL>
        <H3>Warnings & overrides</H3>
        <P>
          You're warned if the official already has an overlapping game, doesn't have enough travel time between venues, is
          already on this game, is inactive, or marked themselves unavailable. You can still go ahead after confirming. The
          override is saved in the game history.
        </P>
        <H3>Working faster</H3>
        <UL>
          <li><B>Bulk</B>: tick several games, then <B>Assign official</B>, <B>Send emails</B> (to everyone still pending) or <B>Remove officials</B>.</li>
          <li><B>Copy crew</B>: copy one game's whole crew onto other games. Availability and conflicts are checked for every game before anything is saved.</li>
        </UL>
        <H3>Correcting past games</H3>
        <P>
          Switch to <B>Past</B> and pick the schedule or league to fix who actually worked a game. Officials you add or swap in on a game that
          has already started are recorded as <B>Accepted</B> straight away. No "please accept" email is sent, and removing someone from a past
          game doesn't email them either. Past games are tagged <B>Played</B>. Fees follow automatically: the new official shows up in
          Payments → <B>Verify</B>, and the official they replaced shows as <B>Replaced</B>. If the game's fees were already approved or paid,
          you're asked to confirm first, and the payment records are flagged for review.
        </P>
        <H3>Game menu</H3>
        <P>
          <B>Replace official</B>, <B>Remove from game</B>, <B>Resend assignment email</B>, <B>Mark accepted (phone / WhatsApp)</B> for answers
          you got outside the app, <B>View history</B>, <B>Cancel game</B> / <B>Restore game</B> and <B>Open schedule</B>.
        </P>
        <Terms rows={[
          ['Pending Acceptance', 'Assigned and emailed, waiting for an answer.'],
          ['Accepted', 'Confirmed by the official (or marked accepted by you).'],
          ['Declined', 'The official said no. You get an email so you can find a replacement.'],
          ['Cancelled', 'Removed, replaced, or the game was cancelled.'],
        ]} />
        <Tip>If you have payment access, each assignment shows its fee and each game shows its total officiating cost.</Tip>
      </>
    ),
  },
  {
    id: 'org-emails',
    part: ORG,
    title: '6. Emails & schedule changes',
    searchText: 'email accept decline buttons decline notice assign a replacement rescheduled game changed reconfirm cancelled game reminders retry',
    body: (
      <>
        <UL>
          <li><B>Assignment email</B>: game details, your instructions, and <B>ACCEPT ASSIGNMENT</B> / <B>DECLINE ASSIGNMENT</B> buttons that work without signing in.</li>
          <li><B>Declines</B>: you (and whoever made the assignment) get an email with an <B>ASSIGN A REPLACEMENT</B> button.</li>
          <li><B>Reschedules</B>: change a game's date, time or venue in the schedule and every assignment updates. Accepted officials go back to Pending and get a "Game changed - please reconfirm" email. Reminders move with the game.</li>
          <li><B>Cancellations</B>: cancelling or deleting a game cancels its assignments and emails the officials. Reminders stop.</li>
          <li><B>Reminders</B>: sent at the times set in Settings, including a nudge to confirm if they haven't answered yet.</li>
          <li>Failed emails are retried automatically. If an official has no email address, mark their answer yourself with <B>Mark accepted</B>.</li>
        </UL>
      </>
    ),
  },
  {
    id: 'org-pay-rates',
    part: ORG,
    title: '7. Payments: fees & rate templates',
    searchText: 'payments tab rates fees per role default sport league tournament official most specific no rate set recalculated templates youth rates save as template apply copy from fill overwrite budget banner',
    body: (
      <>
        <P>
          The <B>Payments</B> tab has nine sections: <B>Overview</B>, <B>Verify</B>, <B>Ledger</B>, <B>Payments</B>, <B>Batches</B>, <B>Rates</B>, <B>Reports</B>,
          <B> Audit trail</B> and <B>Settings</B>. All amounts are calculated exactly, with no rounding drift. The default currency is USD.
        </P>
        <H3>Setting fees</H3>
        <P>Open <B>Rates</B> and set a fee per role at one of four levels. The most specific level wins:</P>
        <OL>
          <li><B>One official</B> (a personal rate)</li>
          <li><B>A league / tournament</B></li>
          <li><B>A sport</B></li>
          <li><B>Everyone (organization default)</B></li>
        </OL>
        <P>
          Leave a box empty to fall back to the next level. Saving recalculates fees that aren't approved or paid yet. Approved and paid
          amounts never change. A role with no fee at any level shows <B>No rate set</B>.
        </P>
        <H3>Rate templates</H3>
        <OL>
          <li>Click <B>New template</B> (optionally <B>Start from a league</B>), or pick a league in the editor and click <B>Save as template</B>.</li>
          <li>Name it (for example "Youth rates 2026"), check the fees per role and add an optional budget.</li>
          <li>Click <B>Apply</B> (or <B>Copy from template</B>) and tick the leagues or tournaments to copy it to.</li>
          <li>If a selected league already has fees, choose <B>Only fill roles that have no fee yet</B> or <B>Overwrite the league's fees</B>.</li>
          <li>Optionally tick <B>Also set the budget</B>. In fill mode, it only sets a budget where none exists.</li>
        </OL>
        <Tip>A banner on the Rates page lists leagues with no fees of their own. Use <B>Copy from…</B> or <B>Copy to all</B> to set them up in seconds. Editing or deleting a template never changes leagues it was already applied to.</Tip>
      </>
    ),
  },
  {
    id: 'org-pay-verify',
    part: ORG,
    title: '8. Payments: verify & approve',
    searchText: 'verify worked did not attend replaced cancelled pending ledger statuses scheduled pending verification earned approved partially paid paid needs review approve change fee approve anyway un-approve mark reviewed cancellation fee',
    body: (
      <>
        <H3>How a fee moves</H3>
        <Terms rows={[
          ['Scheduled', 'Future game. The projected fee.'],
          ['Pending verification', 'The game has been played. Confirm who worked.'],
          ['Earned · needs approval', 'Verified as Worked, waiting for approval.'],
          ['Approved', 'Payable. It counts as outstanding until paid.'],
          ['Partially paid / Paid', 'Part or all of it has been recorded as paid.'],
          ['Did not attend / Replaced / Cancelled', 'Not paid by default.'],
          ['Needs review', 'The game changed after the fee was approved or paid. Check it, then mark it reviewed.'],
        ]} />
        <H3>Verify</H3>
        <P>
          Open <B>Verify</B>. Played games are listed automatically. For each official, choose <B>Worked</B>, <B>Did not attend</B>,
          <B> Replaced</B>, <B>Cancelled</B> or <B>Pending</B>, with an optional note, then <B>Save this game</B>. You can also tick items
          across games and save them all at once. Only <B>Worked</B> becomes payable.
        </P>
        <H3>Approve (Ledger)</H3>
        <UL>
          <li>Filter by league, official, role, status or dates. Tick rows and click <B>Approve</B>, or use a row's menu.</li>
          <li><B>Change fee</B>: overrides the rate for this assignment only, with a reason. <B>Use standard rate</B> undoes it.</li>
          <li><B>Approve payment anyway</B>: for exceptional cases, such as a no-show that should still be paid. A reason is required.</li>
          <li><B>Un-approve</B> moves an unpaid item back to awaiting approval. <B>Mark as reviewed</B> clears a review flag.</li>
        </UL>
        <Tip>Cancelled games pay nothing by default. To pay a short-notice cancellation fee automatically, turn it on in Payments → <B>Settings</B> → <B>Cancellation fees</B> (a percentage of the fee or a flat amount, within a set number of hours before the game).</Tip>
      </>
    ),
  },
  {
    id: 'org-pay-record',
    part: ORG,
    title: '9. Payments: paying officials',
    searchText: 'record payment bank transfer cash cheque other reference partial oldest first reverse batch new batch draft approve batch record payments export delete adjustment travel allowance bonus additional fee deduction manual void statement official ledger',
    body: (
      <>
        <H3>Record a payment</H3>
        <OL>
          <li>Click <B>Pay</B> (Overview), <B>Pay selected</B> (Ledger) or <B>Record payment</B> (Payments, or the official's ledger).</li>
          <li>Check the amount. It defaults to everything owed, and partial payments are fine.</li>
          <li>Pick the date and method (<B>Bank Transfer</B>, <B>Cash</B>, <B>Cheque</B>, <B>Other</B>), and add a reference and notes if you want.</li>
          <li>Leave <B>Email the official a payment confirmation</B> on if you want them notified, then <B>Record payment</B>.</li>
        </OL>
        <P>
          Payments are applied to the oldest approved items first. You can't pay more than is owed, and double-clicking won't create
          duplicates. Made a mistake? <B>Reverse</B> the payment with a reason. It stays in the history marked Reversed, and the amount
          becomes outstanding again.
        </P>
        <H3>Pay a whole period (Batches)</H3>
        <OL>
          <li><B>Batches</B> → <B>New batch</B>: choose a date range, and optionally one league. Every verified or approved-but-unpaid item that isn't already in a batch is collected into a <B>Draft</B>.</li>
          <li>Expand an official to check their items, and remove any that shouldn't be paid yet.</li>
          <li><B>Approve batch</B> approves everything in it.</li>
          <li>Tick the officials to pay, click <B>Record N payments</B>, enter the date, method and reference once, then <B>Confirm payments</B>. Each selected official is paid their full remaining balance.</li>
          <li><B>Export</B> to CSV, Excel or PDF for your bank or treasurer. Only draft batches can be deleted, and their items go back to the ledger.</li>
        </OL>
        <H3>Adjustments</H3>
        <P>
          Use <B>Add adjustment</B> (Ledger, or an official's ledger) for a <B>Travel Allowance</B>, <B>Bonus</B>, <B>Additional Assignment Fee</B>,
          <B> Cancellation Fee</B>, <B>Deduction</B> or <B>Manual Adjustment</B>. Tick <B>Approve now</B> to make it payable straight away.
          Approved and paid amounts are never edited; adjustments keep a clean history. Use <B>Void adjustment</B> to cancel one.
        </P>
        <H3>An official's ledger</H3>
        <P>
          Click any official's name to see their Scheduled, Earned, Approved, Paid and Outstanding totals, every item and payment, a
          <B> Statement</B> download (PDF, CSV or Excel) and <B>Email statement</B>.
        </P>
      </>
    ),
  },
  {
    id: 'org-pay-reports',
    part: ORG,
    title: '10. Overview, reports, budgets & audit',
    searchText: 'overview metrics alerts who needs to be paid reports payment summary outstanding cost per game league expenses budgets official statement export email statements currency notifications audit trail',
    body: (
      <>
        <H3>Overview</H3>
        <P>
          Totals for <B>Scheduled fees</B>, <B>Earned</B>, <B>Approved</B>, <B>Paid</B>, <B>Outstanding</B> and <B>Officials awaiting payment</B>.
          Alerts link straight to games to verify, items to approve or review, roles with no rate, and budget warnings. <B>Who needs to be paid</B>
          lists each official's balance with a <B>Pay</B> button.
        </P>
        <H3>Reports</H3>
        <UL>
          <li><B>Payment summary</B>, <B>Outstanding</B>, <B>Cost per game</B>, <B>League expenses & budgets</B> and <B>Official statement</B>.</li>
          <li>Filter by league, official, role and dates. <B>Export</B> to PDF, CSV or Excel (needs <B>Export reports</B>).</li>
          <li><B>Email statements</B> sends officials their statement for the selected period.</li>
        </UL>
        <H3>Payment settings</H3>
        <UL>
          <li><B>Currency</B> for all fees and reports (USD by default).</li>
          <li><B>Notifications</B>: email officials when earnings are approved and when a payment is recorded, and allow emailing statements.</li>
          <li><B>Budgets</B>: an optional officiating budget per league. The Overview warns you as spend gets close to it or goes over.</li>
        </UL>
        <H3>Audit trail</H3>
        <P>Every rate change, verification, approval, fee change, adjustment, payment, reversal, batch and template action, with who did it and when. Entries can't be edited.</P>
      </>
    ),
  },
  {
    id: 'org-faq',
    part: ORG,
    title: '11. Troubleshooting (organizers)',
    searchText: 'troubleshooting cannot see hub scheduler no payments tab email not received unknown availability warning no rate set fee did not change',
    body: (
      <>
        <Terms rows={[
          ['A scheduler can\'t see the Hub', 'They must sign in with the exact email you added, and the add-on must be active.'],
          ['No Payments tab', 'The owner needs to turn on View payments for that scheduler in Settings → Payment permissions.'],
          ['An official didn\'t get the email', 'Check that their profile has an email address, use Resend assignment email, and ask them to check spam. You can also Mark accepted (phone / WhatsApp).'],
          ['Official shows Unknown', 'They haven\'t shared availability yet. Send an availability request.'],
          ['A fee shows No rate set', 'Add a rate for that role on Payments → Rates, or apply a rate template.'],
          ['The fee didn\'t change after editing a rate', 'Approved and paid amounts are locked. Use Change fee before approval, or an adjustment after.'],
          ['An official isn\'t on the portal', 'They need to accept your invite, or use your sign-up link, with their account.'],
        ]} />
        <P>Still stuck? Ask the assistant (chat bubble, bottom-right) or email <B>support@scheduleguru.ws</B>.</P>
      </>
    ),
  },
  {
    id: 'off-joining',
    part: OFF,
    title: '12. Joining a league',
    searchText: 'official join invite email sign-up link create free account already have an account accept invitation free never pay multiple leagues',
    body: (
      <>
        <P>Officials never pay. All you need is a free Schedule Guru account.</P>
        <OL>
          <li>Open the invite email from your league (<B>JOIN AS AN OFFICIAL</B>), or the sign-up link they shared.</li>
          <li>Choose <B>Create free account</B>, or <B>I already have an account</B> and sign in.</li>
          <li>Check your name, phone, sports and certifications, then <B>Accept invitation</B>.</li>
        </OL>
        <P>
          Your portal is at <Code>/officials/portal</Code>. One account works for every league you officiate for. Each league appears
          in your portal.
        </P>
      </>
    ),
  },
  {
    id: 'off-games',
    part: OFF,
    title: '13. Answering assignments',
    searchText: 'my games waiting for your answer confirmed declined cancelled accept decline reason email buttons without signing in finished game changed reconfirm reminders',
    body: (
      <>
        <UL>
          <li><B>My games</B> lists games <B>Waiting for your answer</B>, <B>Confirmed</B>, <B>Declined</B> and <B>Cancelled</B>, with the date, time, venue, your role and any special instructions.</li>
          <li><B>Accept</B> or <B>Decline</B>. You can add a reason (for example "work conflict") when declining, and your assigner is told straight away.</li>
          <li>The <B>ACCEPT</B> / <B>DECLINE</B> buttons in the email work without signing in.</li>
          <li>If a game's date, time or venue changes, you get a "Game changed - please reconfirm" email. Answer again.</li>
          <li>You get reminder emails before each game.</li>
        </UL>
        <Tip>Once you decline, contact your assigner if you change your mind. Finished games can't be answered.</Tip>
      </>
    ),
  },
  {
    id: 'off-availability',
    part: OFF,
    title: '14. Your availability',
    searchText: 'availability weekly hours specific dates available all day unavailable all day clear day time zone save requests',
    body: (
      <>
        <OL>
          <li>Open <B>Availability</B> in your portal.</li>
          <li><B>Weekly hours</B>: the times you're usually free each day. Leave a day empty if it varies.</li>
          <li><B>Specific dates</B>: tap a date to mark it <B>Available all day</B>, <B>Unavailable all day</B>, set hours, or <B>Clear day</B>. Dates override your weekly hours.</li>
          <li>Click <B>Save</B>. Your league sees it right away when assigning games.</li>
        </OL>
        <P>Times use your time zone, which is shown on the page. When your league sends an availability request, the email brings you straight here.</P>
      </>
    ),
  },
  {
    id: 'off-earnings',
    part: OFF,
    title: '15. Your earnings',
    searchText: 'earnings upcoming completed approved paid owed to you payments received statement pdf csv read-only payment confirmation email',
    body: (
      <>
        <P>The <B>Earnings</B> tab shows what each league owes and has paid you. It's read-only; your league sets the fees.</P>
        <Terms rows={[
          ['Upcoming', 'Fees for games you\'re scheduled for.'],
          ['Completed', 'Games played, awaiting verification or approval.'],
          ['Approved', 'Approved for payment (includes amounts already paid).'],
          ['Paid to you', 'Payments recorded by your league.'],
          ['Owed to you', 'Approved but not yet paid.'],
        ]} />
        <UL>
          <li><B>Games & adjustments</B>: each game, travel allowance, bonus or deduction, with its status.</li>
          <li><B>Payments received</B>: date, method, reference and how many games each payment covered.</li>
          <li><B>Statement</B>: download a PDF or CSV for your records.</li>
          <li>You may also get emails when earnings are approved and when a payment is recorded.</li>
        </UL>
        <Tip>Something look wrong? Contact your league's assigner. Only they can change fees.</Tip>
      </>
    ),
  },
  {
    id: 'off-profile',
    part: OFF,
    title: '16. Profile & FAQ (officials)',
    searchText: 'profile phone photo sports positions certifications managed by league faq not on list link not valid',
    body: (
      <>
        <P>
          In <B>Profile</B> you can update your phone number and photo for each league. Your league manages your sports, positions and
          certifications.
        </P>
        <Terms rows={[
          ['"You\'re not on a league\'s officials list yet"', 'Ask your league for their sign-up link, or open the invite email they sent you.'],
          ['"Link not valid"', 'The link was replaced or has expired. Ask your league for a fresh one.'],
          ['"This game has finished"', 'Answers close once a game is over.'],
          ['Missing an email?', 'Check spam. Your portal always shows the latest assignments.'],
        ]} />
      </>
    ),
  },
];
